import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { ChevronRight, Loader2 } from 'lucide-react';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, breadcrumbLd } from '../seo/Meta.jsx';
import { useLenis } from '../anim/useLenis.js';
import { fetchSampleTenders } from '../lib/api.js';

/**
 * Sample  -  live JSON browser. Pulls /v1/eu-tenders/samples and renders
 * a real two-column table + pretty-printed JSON. No auth (ADR 0007).
 * Same JSON shape the customer gets after signup, so this page IS the
 * contract.
 */
export default function Sample() {
  useLenis();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchSampleTenders()
      .then((list) => {
        setSamples(list);
        if (list[0]) setActive(list[0]);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Meta
        title="Live tender samples  -  see the JSON shape"
        description="Browse real EU tender records pulled live from the tender-api production API. Same JSON shape you receive after signup. No login, no card."
        path="/sample"
        jsonLd={[
          breadcrumbLd([
            { name: 'tender-api', path: '/' },
            { name: 'Live samples', path: '/sample' },
          ]),
        ]}
      />
      <Header />
      <main className="container" style={{ paddingTop: 64, paddingBottom: 96 }}>
        <h1 className="hero-headline mono" style={{ fontSize: 'clamp(36px, 5vw, 60px)' }}>
          Live tender samples
        </h1>
        <p className="lede" style={{ maxWidth: 760, marginTop: 16 }}>
          Pulled live from the production API right now. No signup, no auth  -  the
          same JSON shape you'll get after subscribing. Click any row to inspect.
        </p>

        {loading && (
          <div style={{ marginTop: 48, color: 'var(--fg-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={16} className="spin" /> loading samples…
          </div>
        )}
        {err && (
          <div style={{ marginTop: 48, color: 'var(--danger)' }}>{err}</div>
        )}

        {!loading && samples.length > 0 && (
          <div className="sample-grid">
            <div className="sample-table-wrap">
              <table className="sample-table">
                <thead>
                  <tr>
                    <Th>Country</Th>
                    <Th>Buyer</Th>
                    <Th>Title</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {samples.map((s, i) => (
                    <motion.tr
                      key={s.notice_id || s.id || i}
                      onClick={() => setActive(s)}
                      className={active?.notice_id === s.notice_id ? 'is-active' : ''}
                      initial={{ opacity: 0, y: 4 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, ease: [0.7, 0, 0.15, 1], delay: Math.min(i * 0.025, 0.4) }}
                    >
                      <Td>
                        <Icon icon={`flag:${(s.country || 'eu').toLowerCase()}-4x3`} inline style={{ fontSize: 14, marginRight: 6 }} />
                        {s.country}
                        {s._sample && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 4px', background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 3 }}>_sample</span>}
                      </Td>
                      <Td>{truncate(s.buyer?.name || '', 28)}</Td>
                      <Td>{truncate(s.title?.values?.[s.title?.primary_lang] || '', 36)}</Td>
                      <Td><ChevronRight size={14} color="var(--fg-3)" /></Td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="sample-json">
              {active && (
                <pre>
                  {JSON.stringify(active, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}
function Th({ children }) {
  return (
    <th className="mono" style={{ padding: '10px 12px', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)' }}>
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 13 }}>{children}</td>;
}
